import { useState, useRef, useEffect } from "react";
import { ChevronDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { COUNTRY_CODES, parsePhoneFromUrl, buildPhoneUrl } from "@/data/countryCodes";

interface PhoneInputProps {
  value:       string;
  onChange:    (val: string) => void;
  platform:    "phone" | "whatsapp";
  placeholder?: string;
}

export default function PhoneInput({ value, onChange, platform, placeholder }: PhoneInputProps) {
  const parsed = parsePhoneFromUrl(value, platform);
  const [countryCode, setCountryCode] = useState(parsed.code);
  const [number, setNumber]           = useState(parsed.number);
  const [open, setOpen]               = useState(false);
  const [search, setSearch]           = useState("");
  const dropdownRef                   = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Sync when external value changes
  useEffect(() => {
    const p = parsePhoneFromUrl(value, platform);
    setCountryCode(p.code);
    setNumber(p.number);
  }, [value]);

  const handleCountrySelect = (code: string) => {
    setCountryCode(code);
    setOpen(false);
    setSearch("");
    onChange(buildPhoneUrl(code, number, platform));
  };

  const handleNumberChange = (num: string) => {
    const digits = num.replace(/\D/g, "");
    setNumber(digits);
    onChange(buildPhoneUrl(countryCode, digits, platform));
  };

  const filtered = search
    ? COUNTRY_CODES.filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.code.includes(search) ||
        c.iso.toLowerCase().includes(search.toLowerCase())
      )
    : COUNTRY_CODES;

  const selected = COUNTRY_CODES.find((c) => c.code === countryCode && c.iso) || COUNTRY_CODES[0];

  return (
    <div className="flex gap-2 relative" ref={dropdownRef}>
      {/* Country code selector */}
      <button
        type="button"
        onClick={() => { setOpen(!open); setSearch(""); }}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 border-border hover:border-primary bg-background transition-all text-xs font-medium shrink-0 min-w-[100px] justify-between"
      >
        <span className="flex items-center gap-1.5">
          <span>{selected.flag}</span>
          <span className="text-muted-foreground">{countryCode}</span>
        </span>
        <ChevronDown className={`w-3 h-3 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 bg-white border border-border rounded-2xl shadow-2xl w-72">
          {/* Search */}
          <div className="p-2 border-b border-border">
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-muted/50">
              <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country..."
                className="bg-transparent outline-none text-xs flex-1 text-foreground placeholder:text-muted-foreground"
              />
            </div>
          </div>
          {/* List */}
          <div className="max-h-56 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <p className="text-center text-xs text-muted-foreground py-4">No results found</p>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.iso}
                  type="button"
                  onClick={() => handleCountrySelect(c.code)}
                  className={`flex items-center gap-2 w-full px-3 py-2 rounded-xl text-xs hover:bg-primary/5 text-left transition-colors ${
                    c.code === countryCode ? "bg-primary/10 text-primary font-medium" : ""
                  }`}
                >
                  <span className="text-base">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-muted-foreground shrink-0">{c.code}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {/* Phone number input */}
      <Input
        value={number}
        onChange={(e) => handleNumberChange(e.target.value)}
        placeholder={placeholder || (selected.format ? selected.format.replace(/X/g, "0") : "50 123 4567")}
        className="flex-1 rounded-xl bg-background border-2 border-border focus:border-primary text-xs"
        type="tel"
        inputMode="numeric"
      />
    </div>
  );
}